import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { MoreHorizontal, MoreVertical } from 'lucide-react';

import { Tooltip } from '@/components/Tooltip/Tooltip';

import styles from './ContentUnitKebabMenu.module.css';

interface ContentUnitKebabMenuProps {
  canMoveDown: boolean;
  canMoveUp: boolean;
  disabled?: boolean;
  disabledReason?: string;
  onCopy?: () => void;
  onDelete: () => void;
  onMoveDown: () => void;
  onMoveUp: () => void;
  triggerClassName?: string;
  triggerIcon?: 'horizontal' | 'vertical';
  unitLabel: string;
}

export function ContentUnitKebabMenu({
  canMoveDown,
  canMoveUp,
  disabled = false,
  disabledReason,
  onCopy,
  onDelete,
  onMoveDown,
  onMoveUp,
  triggerClassName,
  triggerIcon = 'vertical',
  unitLabel,
}: ContentUnitKebabMenuProps) {
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const TriggerIcon = triggerIcon === 'horizontal' ? MoreHorizontal : MoreVertical;
  const trigger = (
    <span className={styles.tooltipAnchor}>
      <DropdownMenu.Trigger asChild>
        <button
          aria-label={`${unitLabel} options`}
          className={triggerClassName ?? styles.trigger}
          disabled={disabled}
          type="button"
        >
          <TriggerIcon aria-hidden="true" size={18} strokeWidth={2} />
        </button>
      </DropdownMenu.Trigger>
    </span>
  );

  return (
    <>
      <DropdownMenu.Root modal={false}>
        {/* A tooltip only to say why the menu cannot open. A "More options"
            one opened again with the focus that Escape gives back to the
            trigger, and stayed over the page's Save button. A disabled button
            takes no pointer events, so the reason hangs on what wraps it. */}
        {disabled ? (
          <Tooltip content={disabledReason ?? 'Save or cancel the current edit first.'}>
            {trigger}
          </Tooltip>
        ) : (
          trigger
        )}
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            className={styles.menuContent}
            sideOffset={8}
          >
            {onCopy ? (
              <>
                <DropdownMenu.Item
                  className={styles.menuItem}
                  onSelect={onCopy}
                >
                  Copy
                </DropdownMenu.Item>
                <DropdownMenu.Separator className={styles.separator} />
              </>
            ) : null}
            <DropdownMenu.Item
              className={styles.menuItem}
              disabled={!canMoveUp}
              onSelect={onMoveUp}
            >
              Move up
            </DropdownMenu.Item>
            <DropdownMenu.Item
              className={styles.menuItem}
              disabled={!canMoveDown}
              onSelect={onMoveDown}
            >
              Move down
            </DropdownMenu.Item>
            <DropdownMenu.Separator className={styles.separator} />
            <DropdownMenu.Item
              className={[styles.menuItem, styles.dangerMenuItem].join(' ')}
              onSelect={(event) => {
                event.preventDefault();
                setIsDeleteOpen(true);
              }}
            >
              Delete
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      <Dialog.Root onOpenChange={setIsDeleteOpen} open={isDeleteOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className={styles.dialogOverlay} />
          <Dialog.Content className={styles.dialogContent}>
            <Dialog.Title className={styles.dialogTitle}>
              Delete {unitLabel.toLowerCase()}?
            </Dialog.Title>
            <Dialog.Description className={styles.dialogDescription}>
              Delete this item? You can undo by canceling page edits before
              saving.
            </Dialog.Description>
            <div className={styles.dialogActions}>
              <Dialog.Close asChild>
                <button className={styles.cancelButton} type="button">
                  Cancel
                </button>
              </Dialog.Close>
              <button
                className={styles.deleteButton}
                onClick={() => {
                  onDelete();
                  setIsDeleteOpen(false);
                }}
                type="button"
              >
                Delete
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}
